/**
 * Export validation.
 *
 * These are the checks Apple's rules reduce to, and they exist as code because every one of
 * them has a real failure behind it: an alpha channel left in by a compositing step, a
 * screenshot at the wrong pixel size, a set over ten panels, a caption sitting on top of a
 * device.
 *
 * The collision check is the one that needed a hand-written pass in the previous pipeline:
 * an approved caption overlapped a device body and reached a listing. Here both came from
 * the same renderer run, so the check is exact rather than approximate.
 */
import type { CanvasSize, OutputFamily, SceneDoc } from '@keymaker/schema';
import { collectTokens } from '@keymaker/schema';
import type { LayerBounds } from './render';
import type { Rect } from './fit';

export interface ExportProblem {
  code:
    | 'wrong-size'
    | 'not-opaque'
    | 'too-many-panels'
    | 'text-device-collision'
    | 'text-off-canvas'
    | 'missing-token'
    | 'duplicate-token';
  message: string;
  layerId?: string;
}

export interface RgbaStats {
  /** Pixels with alpha below 255. */
  transparentPixels: number;
  totalPixels: number;
  /** Fraction of pixels that are fully opaque. */
  opaqueRatio: number;
}

/** Measure opacity from raw RGBA. Apple rejects screenshots with transparency. */
export function rgbaStats(rgba: Uint8Array, width: number, height: number): RgbaStats {
  const totalPixels = width * height;
  let transparentPixels = 0;
  for (let i = 3; i < rgba.length; i += 4) {
    if (rgba[i] !== 255) transparentPixels += 1;
  }
  return {
    transparentPixels,
    totalPixels,
    opaqueRatio: totalPixels === 0 ? 0 : (totalPixels - transparentPixels) / totalPixels,
  };
}

/** True when the two rects share any area. */
export function rectsOverlap(a: Rect, b: Rect): boolean {
  return (
    a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height
  );
}

export function overlapArea(a: Rect, b: Rect): number {
  const width = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
  const height = Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y);
  return width > 0 && height > 0 ? width * height : 0;
}

export interface ValidateOptions {
  /** Ignore collisions smaller than this fraction of the text layer's box. */
  collisionTolerance?: number;
}

/**
 * Check a rendered panel against the family it was rendered for.
 *
 * Deliberately takes the renderer's own `bounds` rather than recomputing geometry: a check
 * that derives its own numbers can pass while the render is wrong.
 */
export function validatePanel(
  input: {
    scene: SceneDoc;
    canvas: CanvasSize;
    bounds: readonly LayerBounds[];
    rgba?: Uint8Array;
  },
  family: OutputFamily,
  options: ValidateOptions = {},
): ExportProblem[] {
  const problems: ExportProblem[] = [];
  const tolerance = options.collisionTolerance ?? 0.02;

  if (
    input.canvas.width !== family.canvas.width ||
    input.canvas.height !== family.canvas.height
  ) {
    problems.push({
      code: 'wrong-size',
      message:
        `Rendered ${input.canvas.width}x${input.canvas.height} but family "${family.key}" requires ` +
        `${family.canvas.width}x${family.canvas.height}.`,
    });
  }

  if (input.rgba) {
    const stats = rgbaStats(input.rgba, input.canvas.width, input.canvas.height);
    if (stats.transparentPixels > 0) {
      problems.push({
        code: 'not-opaque',
        message:
          `${stats.transparentPixels} of ${stats.totalPixels} pixels are not fully opaque ` +
          `(${((1 - stats.opaqueRatio) * 100).toFixed(2)}% transparent). App Store screenshots must be opaque.`,
      });
    }
  }

  const textLayers = input.bounds.filter((b) => b.type === 'text');
  const occluders = input.bounds.filter((b) => b.occludes);

  // Overlaps a template declared on purpose. Collected here so a full-bleed layout does not
  // force the whole check to be relaxed.
  const declaredOverlaps = new Set<string>();
  for (const layer of input.scene.layers) {
    if (layer.type !== 'text' || layer.allowOverlapWith.length === 0) continue;
    for (const other of layer.allowOverlapWith) declaredOverlaps.add(`${layer.id}\u0000${other}`);
  }

  for (const text of textLayers) {
    for (const occluder of occluders) {
      if (declaredOverlaps.has(`${text.id}\u0000${occluder.id}`)) continue;
      // Only layers painted *after* this caption sit on top of it. A panel or device underneath
      // cannot cover it, and flagging that was producing false positives on every layout that
      // puts a caption on a coloured panel.
      if (occluder.order <= text.order) continue;
      const area = overlapArea(text.rect, occluder.rect);
      if (area === 0) continue;
      const fraction = area / Math.max(text.rect.width * text.rect.height, 1);
      if (fraction <= tolerance) continue;
      problems.push({
        code: 'text-device-collision',
        message:
          `Text layer "${text.id}" is covered by "${occluder.id}" over ${(fraction * 100).toFixed(1)}% ` +
          'of its box. Move the caption, shrink the device, or rewrap.',
        layerId: text.id,
      });
    }

    const outsideX = text.rect.x < 0 || text.rect.x + text.rect.width > input.canvas.width;
    const outsideY = text.rect.y < 0 || text.rect.y + text.rect.height > input.canvas.height;
    if (outsideX || outsideY) {
      problems.push({
        code: 'text-off-canvas',
        message: `Text layer "${text.id}" extends past the canvas edge and would be clipped.`,
        layerId: text.id,
      });
    }
  }

  return problems;
}

/**
 * Check the tokens a scene depends on against the copy supplied for a locale.
 *
 * A missing token renders as literal `{{caption.3}}` in a shipped screenshot, which is both
 * embarrassing and easy to miss across fifty locales. Surfacing it here makes it a build
 * failure instead.
 */
export function validateTokens(scene: SceneDoc, available: ReadonlySet<string>): ExportProblem[] {
  const problems: ExportProblem[] = [];
  const seen = new Set<string>();

  for (const token of collectTokens(scene)) {
    if (seen.has(token)) {
      problems.push({ code: 'duplicate-token', message: `Token "{{${token}}}" is used more than once.` });
    }
    seen.add(token);
    if (!available.has(token)) {
      problems.push({
        code: 'missing-token',
        message: `Scene references "{{${token}}}" but no value was supplied for this locale.`,
      });
    }
  }

  return problems;
}

/** Substitute `{{token}}` placeholders. Unknown tokens are left visible on purpose. */
export function substituteTokens(scene: SceneDoc, values: ReadonlyMap<string, string>): SceneDoc {
  const pattern = /\{\{\s*([\w.[\]-]+)\s*\}\}/g;
  return {
    ...scene,
    layers: scene.layers.map((layer) =>
      layer.type === 'text'
        ? { ...layer, content: layer.content.replace(pattern, (whole, key: string) => values.get(key) ?? whole) }
        : layer,
    ),
  };
}
